import React from "react";
import { useSelector } from "react-redux";
import {
  Button,
  Box,
  Grid,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import axios from "axios";

import ToExcel from "icons/Toexcel";

function UpdateTransactions() {
  const [date, setDate] = React.useState(new Date());
  const [submitDisable, setSubmitDisable] = React.useState(false);
  const [allStores, setAllStores] = React.useState(false);
  const [rows, setRows] = React.useState({
    detailed: [],
    total: {
      total_sales: 0,
      total_budget: 0,
      total_percent: 0,
      total_transactions: 0,
      total_average: 0,
    },
  });
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [isExcelLoading, setIsExcelLoading] = React.useState(false);
  const [alert, setAlert] = React.useState(false);
  const [uploadResponse, setUploadResponse] = React.useState("");
  const [severity, setSeverity] = React.useState("info");

  const handleDateChange = (date) => {
    setDate(date);
    setSubmitDisable(false);
  };

  const fetchData = async (date) => {
    try {
      const response = await axios.get(
        "/api/budgetReport/day_store_sales_budget/",
        {
          params: {
            date: date,
          },
          timeout: 1200000, // 10 minutes timeout for long-running backend process
        },
      );
      console.log(typeof response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching data:", error);
      throw error;
    }
  };

  const importExcel = async (date) => {
    try {
      const response = await axios.get(
        "/api/budgetReport/stores_sales_budget_web/",
        {
          params: {
            date: date.toISOString().split("T")[0],
          },
          responseType: "blob",
        },
      );
      return response;
    } catch (error) {
      console.error("Error fetching data:", error);
      throw error;
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setIsLoaded(true);
    setAlert(false);
    fetchData(date)
      .then((data) => {
        setIsLoaded(false);
        setAlert(true);
        setSeverity("success");
        setUploadResponse("Update successful");
        setSubmitDisable(true);
        if (data && data.detailed) {
          setRows(data);
        }
      })
      .catch((error) => {
        setIsLoaded(false);
        setAlert(true);
        setSeverity("error");
        setUploadResponse("Update failed");
        setSubmitDisable(true);
      });
  };

  const downloadExcel = async (event) => {
    event.preventDefault();
    setAlert(false);
    setIsExcelLoading(true);
    try {
      const response = await importExcel(date);

      // Extract filename from Content-Disposition header
      let filename = "report.xlsx"; // Default filename
      const contentDisposition = response.headers["content-disposition"];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/"/g, ""); // Remove any quotes
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading Excel file:", error);
    } finally {
      setIsExcelLoading(false);
    }
  };

  const defaultStatus = [];

  return (
    <>
      <Paper
        elevation={5}
        style={{ padding: "6px", margin: "15px 25px 10px 20px" }}
      >
        <form noValidate onSubmit={handleSubmit}>
          <Box pt={5} pb={5} pl={2}>
            <Grid container spacing={7}>
              <Grid item md={2} xs={2}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    disableFuture
                    minDate={new Date(2025, 0, 1)}
                    label="Date"
                    value={date}
                    onChange={handleDateChange}
                    slotProps={{ textField: { variant: "outlined" } }}
                  ></DatePicker>
                </LocalizationProvider>
              </Grid>

              <Grid item style={{ marginTop: 22, paddingRight: 14 }}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={submitDisable}
                >
                  Update
                </Button>
              </Grid>
              <Grid item style={{ marginTop: 22 }}>
                <Button
                  variant="contained"
                  startIcon={<ToExcel />}
                  onClick={(event) => downloadExcel(event, false)}
                ></Button>
              </Grid>
              <Grid item md={3} xs={3} style={{ marginTop: 22 }}>
                {isLoaded && (
                  <Box display="flex" alignItems="center">
                    <CircularProgress color="secondary" size={20} />
                    <Typography
                      variant="body2"
                      style={{ marginLeft: 10, color: "gray" }}
                    >
                      Updating database...
                    </Typography>
                  </Box>
                )}
                {isExcelLoading && (
                  <Box display="flex" alignItems="center">
                    <CircularProgress color="secondary" size={20} />
                    <Typography
                      variant="body2"
                      style={{ marginLeft: 10, color: "gray" }}
                    >
                      Extracting Excel report...
                    </Typography>
                  </Box>
                )}
                {alert && <Alert severity={severity}> {uploadResponse} </Alert>}
              </Grid>
            </Grid>
          </Box>
        </form>
      </Paper>
      {rows.detailed.length > 0 && (
        <Paper
          elevation={3}
          style={{ padding: "6px", margin: "15px 25px 10px 20px" }}
        >
          <Grid
            container
            justifyContent="space-between"
            spacing={4}
            sx={{ px: 3, py: 4 }}
          >
            <Grid item md={2} xs={12}>
              <TextField
                fullWidth
                label="Total Sales"
                value={rows.total.total_sales?.toLocaleString() || ""}
                InputProps={{
                  readOnly: true,
                }}
                variant="outlined" // or "filled" or "standard"
              />
            </Grid>
            <Grid item md={2} xs={12}>
              <TextField
                fullWidth
                label="Total Budget"
                value={rows.total.total_budget?.toLocaleString() || ""}
                InputProps={{
                  readOnly: true,
                }}
                variant="outlined" // or "filled" or "standard"
              />
            </Grid>
            <Grid item md={2} xs={12}>
              <TextField
                fullWidth
                label="Total %"
                value={rows.total.total_percent}
                InputProps={{
                  readOnly: true,
                }}
                variant="outlined" // or "filled" or "standard"
              />
            </Grid>
            <Grid item md={2} xs={12}>
              <TextField
                fullWidth
                label="Total Transactions"
                value={rows.total.total_transactions?.toLocaleString() || ""}
                InputProps={{
                  readOnly: true,
                }}
                variant="outlined" // or "filled" or "standard"
              />
            </Grid>
            <Grid item md={2} xs={12}>
              <TextField
                fullWidth
                label="Total Average"
                value={rows.total.total_average}
                InputProps={{
                  readOnly: true,
                }}
                variant="outlined" // or "filled" or "standard"
              />
            </Grid>
          </Grid>
        </Paper>
      )}
    </>
  );
}

export default UpdateTransactions;
